/**
 * Фоновая синхронизация.
 *
 * Очередь разбирается при загрузке приложения, при возвращении связи и после
 * каждой завершённой тренировки. Ни одна из этих операций не блокирует ввод
 * (ТЗ FR-SY-04, FR-SY-05).
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { EntitlementSnapshot } from '@type2x/shared-contracts';
import {
  SyncQueue,
  enqueueSession,
  restoreQueue,
  synchronise,
  type SessionPayload,
} from '@type2x/feature-sync';

import type { Runtime } from '../runtime.js';

/** Возможности синхронизации, доступные приложению. */
export interface SyncControls {
  /** Ставит итог сессии в очередь и пытается отправить его. */
  readonly submit: (payload: SessionPayload) => Promise<void>;
  /**
   * Разбирает очередь; `false` — разобрать удалось не всё.
   *
   * Ответ нужен выходу из аккаунта: он забывает локальное пространство, и
   * неотправленные итоги занятий вместе с ним не вернутся ниоткуда.
   */
  readonly flush: () => Promise<boolean>;
}

/**
 * @param runtime зависимости приложения;
 * @param onEntitlements вызывается, когда сервер прислал снимок доступов.
 */
export function useSync(
  runtime: Runtime,
  onEntitlements: (snapshot: EntitlementSnapshot) => void,
): SyncControls {
  const queue = useMemo(() => new SyncQueue(), []);
  const entitlementsRef = useRef(onEntitlements);
  entitlementsRef.current = onEntitlements;

  const flush = useCallback(async () => {
    try {
      const outcome = await synchronise(queue, runtime.queueRepository, runtime.sync);
      if (outcome.entitlements !== null) entitlementsRef.current(outcome.entitlements);
      return !outcome.waiting;
    } catch {
      // Синхронизация никогда не должна мешать обучению: неудача остаётся
      // здесь, а операции — в очереди.
      return false;
    }
  }, [queue, runtime]);

  const submit = useCallback(
    async (payload: SessionPayload) => {
      enqueueSession(queue, payload, runtime.clock.isoNow());
      await flush();
    },
    [queue, runtime, flush],
  );

  // Восстановление очереди и первая попытка отправки при запуске.
  useEffect(() => {
    let cancelled = false;

    restoreQueue(queue, runtime.queueRepository)
      .then(() => {
        if (!cancelled) void flush();
      })
      .catch(() => {
        // Хранилище недоступно: очередь начинается пустой.
      });

    return () => {
      cancelled = true;
    };
  }, [queue, runtime, flush]);

  // Возвращение связи — единственный момент, когда повтор заведомо осмыслен.
  useEffect(() => {
    const onOnline = (): void => {
      void flush();
    };

    globalThis.addEventListener('online', onOnline);
    return () => globalThis.removeEventListener('online', onOnline);
  }, [flush]);

  return { submit, flush };
}
